import {Box, HStack, Icon, ThemeIconKey, Text, ThemeColorPath} from 'platform/foundation';

import {RecordAuditResultValue} from '@dms/api/core';
import i18n from '@dms/i18n';

interface PillSpec {
  value: RecordAuditResultValue;
  labelKey: string;
  icon: ThemeIconKey;
  color: ThemeColorPath;
}

const OPTIONS: ReadonlyArray<PillSpec> = [
  {
    value: 'OK',
    labelKey: 'page.checklistAudits.detail.value.OK',
    icon: 'navigation/check',
    color: 'severity.success',
  },
  {
    value: 'BAD',
    labelKey: 'page.checklistAudits.detail.value.BAD',
    icon: 'navigation/close',
    color: 'severity.danger',
  },
  {
    value: 'NA',
    labelKey: 'page.checklistAudits.detail.value.NA',
    icon: 'content/remove',
    color: 'palettes.neutral.70.100',
  },
];

interface OkBadNaRadioProps {
  value: RecordAuditResultValue | null | undefined;
  onChange: (next: RecordAuditResultValue | null) => void;
  isReadonly?: boolean;
  allowNa?: boolean;
}

export function OkBadNaRadio(props: OkBadNaRadioProps) {
  const allowNa = props.allowNa ?? true;
  const filteredOptions = allowNa ? OPTIONS : OPTIONS.filter((option) => option.value !== 'NA');
  const visibleOptions = props.isReadonly
    ? filteredOptions.filter((option) => option.value === props.value)
    : filteredOptions;

  return (
    <HStack spacing={2}>
      {visibleOptions.map((option) => (
        <AnswerPill
          key={option.value}
          option={option}
          isSelected={props.value === option.value}
          isDisabled={props.isReadonly}
          onClick={() => props.onChange(props.value === option.value ? null : option.value)}
        />
      ))}
    </HStack>
  );
}

interface AnswerPillProps {
  option: PillSpec;
  isSelected: boolean;
  isDisabled?: boolean;
  onClick: () => void;
}

function AnswerPill(props: AnswerPillProps) {
  const ariaPressed = props.isSelected;
  const cursor = props.isDisabled ? 'default' : 'pointer';

  return (
    <button
      type="button"
      aria-pressed={ariaPressed}
      onClick={props.onClick}
      disabled={props.isDisabled}
      // eslint-disable-next-line react/forbid-dom-props -- raw <button> reset; no platform primitive
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor,
      }}
    >
      <Box
        paddingHorizontal={3}
        paddingVertical={1}
        borderRadius="medium"
        border="1px solid"
        borderColor={props.option.color}
        backgroundColor={props.isSelected ? props.option.color : 'general.white'}
      >
        <HStack spacing={2} align="center">
          <Icon
            value={props.option.icon}
            size={4}
            color={props.isSelected ? 'general.white' : props.option.color}
          />
          <Text size="small" color={props.isSelected ? 'white' : 'primary'} noWrap>
            {i18n.t(props.option.labelKey)}
          </Text>
        </HStack>
      </Box>
    </button>
  );
}
